import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/lib/toast';
import { captureMutationError } from '@/lib/sentry';

export function useAssignTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, agentId }: { taskId: string; agentId: string }) =>
      api.assignTask(taskId, agentId),

    onSuccess: (_result, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(taskId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.unassigned() });
      toast.success('Task assigned.');
    },

    onError: (error, { taskId, agentId }) => {
      captureMutationError(error, { mutation: 'useAssignTask', taskId, agentId });
      toast.error('Failed to assign task. Please try again.');
    },
  });
}

export function useDeclineTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId }: { taskId: string }) => api.declineTask(taskId),

    onSuccess: (_result, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(taskId) });
      toast.success('Task declined.');
    },

    onError: (error, { taskId }) => {
      captureMutationError(error, { mutation: 'useDeclineTask', taskId });
      toast.error('Failed to decline task. Please try again.');
    },
  });
}

export function useBulkAssignTasks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskIds, agentId }: { taskIds: string[]; agentId: string }) =>
      api.bulkAssignTasks(taskIds, agentId),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.unassigned() });
      toast.success('Tasks assigned successfully.');
    },

    onError: (error, { taskIds, agentId }) => {
      captureMutationError(error, { mutation: 'useBulkAssignTasks', taskCount: taskIds.length, agentId });
      toast.error('Failed to assign tasks. Please try again.');
    },
  });
}
